import { call, race, select, take, put } from 'redux-saga/effects'
import toBlob from 'canvas-to-blob'
import { reset, change } from 'redux-form'

import actions from 'actions'
import api from 'services'
import { entityUpdate, hideModal } from 'actions/common'
import { fetchData, fetchEntity } from 'utils/sagas'
import { getCurrentPartnerId } from 'selectors/settings'
import { getSetupform, } from 'selectors/entities'
import { getImageId, getCurrentSetupformId, getCurrentSetupForm } from 'selectors/setupform'
import { redirectAbsolutePath } from 'utils/redirect'
import { getSetupformUrl, fromTicketing, getPartnerUrl, isSummary } from 'utils/urls'
import { REVIEW_STATUS, STAGE_STATUS } from 'constants/setupform-constants'
import { EventGroupPurchaseTypes } from 'constants/eventgroup-constants'
import { push } from 'react-router-redux'

// entity fetchers
export const fetchEventTypes = fetchData.bind(null, actions.eventType, api.fetchEventTypes)
export const fetchSetupform = fetchEntity.bind(null, actions.setupform, api.fetchSetupform)
export const updateSetupform = fetchEntity.bind(null, actions.setupform, api.updateSetupform)
export const submitSetupform = fetchEntity.bind(null, actions.setupform, api.submitSetupform)
export const apiDeleteSetupform = fetchEntity.bind(null, actions.setupform, api.deleteSetupform)
export const fetchVenue = fetchEntity.bind(null, actions.venue, api.fetchVenue)
export const fetchVenueSearchResults = fetchData.bind(null, actions.venueSearchResults, api.fetchVenueSearchResults)
export const uploadImage = fetchData.bind(null, actions.uploadImage, api.uploadImage)
export const deleteImage = fetchData.bind(null, actions.deleteImage, api.deleteImage)
export const loadImage = fetchData.bind(null, actions.loadImage, api.loadImage)
export const createNewVenue = fetchData.bind(null, actions.createNewVenue, api.createNewVenue)
export const bulkUpdateSetupformInstanceGroups = fetchData.bind(
  null,
  actions.bulkUpdateSetupformInstanceGroups,
  api.bulkUpdateSetupformInstanceGroups
)


// sagas
export function* loadEventTypes() {
  yield call(fetchEventTypes)
}

export function* loadSetupform({ setupformId }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  yield call(fetchSetupform, setupformId, partnerId)

  // also load the venue and setupform image if they exist
  const currentVenueId = (yield select(getSetupform(setupformId))).venueId
  if (currentVenueId) {
    yield call(fetchVenue, partnerId, currentVenueId)
  }
  yield call(loadImage, partnerId, setupformId)
}

export function* setupformLoadSuccessSaga({ setupformId }) {
  const setupform = yield select(getSetupform(setupformId))

  if (setupform) {
    const partnerId = parseInt(yield select(getCurrentPartnerId), 10)

    if (!isSummary() && setupform.reviewStatus === REVIEW_STATUS.SUBMITTED) {
      redirectAbsolutePath(getSetupformUrl(partnerId, setupformId, STAGE_STATUS.SUMMARY), true)
    }
  }
}

export function* deleteSetupform({ setupformId }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  yield call(apiDeleteSetupform, setupformId, partnerId)
}

export function* updateSetupformBasicDetails({ setupformId, values, resolve, reject }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)

  // call updateSetupform with no image data as we are uploading separately
  const { response, error } = yield race({
    call: call(updateSetupform, setupformId, partnerId, { ...values, image: undefined }, resolve, reject),
    response: take(actions.ENTITIES_SETUPFORM.SUCCESS),
    error: take(actions.ENTITIES_SETUPFORM.FAILURE),
  })

  // create the new venue if one exists
  if (values.newVenue) {
    yield call(createNewVenue, partnerId, setupformId, values.newVenue)
  }

  if (response) {
    // hooray, it saved!
    const setupform = (yield select(getSetupform(setupformId)))
    if (setupform.purchaseType === EventGroupPurchaseTypes.VOUCHER) {
      const url = getSetupformUrl(partnerId, setupformId, STAGE_STATUS.VOUCHERS)
      yield put(push(url))
    } else {
      resolve()
      const path = `${getSetupformUrl(partnerId, setupformId, STAGE_STATUS.CALENDAR)}?first_event=${fromTicketing()}`
      redirectAbsolutePath(path)
    }
  } else {
    reject(error)
  }
}

export function* updateSetupformAdvancedDetails({ setupformId, values, resolve, reject }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)

  const { response, error } = yield race({
    call: call(updateSetupform, setupformId, partnerId, { ...values }, resolve, reject),
    response: take(actions.ENTITIES_SETUPFORM.SUCCESS),
    error: take(actions.ENTITIES_SETUPFORM.FAILURE),
  })

  if (response) {
    resolve()
    redirectAbsolutePath(getSetupformUrl(partnerId, setupformId, STAGE_STATUS.SUMMARY))
  } else {
    reject(error)
  }
}

export function* copyVoucher({ setupformId, instanceGroupId }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const setupForm = (yield select(getSetupform(setupformId)))
  const instanceGroupToCopy = setupForm.instanceGroups.find(ig => ig.id === instanceGroupId)
  const apiActions = {
    actions: [
      {
        type: "create",
        data: {
          start_time: instanceGroupToCopy.instances[0].voucherValidTo,
          ticket_count: instanceGroupToCopy.ticketCount,
          instances: [
            {
              offsale_time: instanceGroupToCopy.instances[0].offsaleTime,
              onsale_time: instanceGroupToCopy.instances[0].onsaleTime,
              voucher_valid_from: instanceGroupToCopy.instances[0].voucherValidFrom,
              voucher_valid_to: instanceGroupToCopy.instances[0].voucherValidTo,
              initial_price: instanceGroupToCopy.instances[0].initialPrice,
              discount_price: instanceGroupToCopy.instances[0].discountPrice,
              ticket_type: `${instanceGroupToCopy.instances[0].ticketType} (copy)`,
              ticket_increment: "1",
              commission: instanceGroupToCopy.instances[0].commission,
            }
          ]
        }
      }
    ]
  }

  yield race({
    call: call(bulkUpdateSetupformInstanceGroups, setupformId, partnerId, apiActions),
    response: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.SUCCESS),
    error: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.FAILURE),
  })
}

export function* deleteVoucher({ setupformId, instanceGroupId }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const apiActions = {
    actions: [
      {
        type: "delete",
        id: instanceGroupId
      }
    ]
  }
  const { response } = yield race({
    call: call(bulkUpdateSetupformInstanceGroups, setupformId, partnerId, apiActions),
    response: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.SUCCESS),
    error: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.FAILURE),
  })

  if (response) {
    const setupForm = (yield select(getSetupform(setupformId)))
    const indexToRemove = setupForm.instanceGroups.findIndex(ig => ig.id === instanceGroupId)

    if (indexToRemove >= 0) {
      const modifiedSetupForm = {
        ...setupForm,
        instanceGroups: [...setupForm.instanceGroups]
      }
      modifiedSetupForm.instanceGroups.splice(indexToRemove, 1)
      const setupFormMap = {}
      setupFormMap[modifiedSetupForm.id] = modifiedSetupForm
      yield put(
        entityUpdate(
          {
            response: {
              replaceEntities: {
                setupforms: setupFormMap
              }
            }
          }
        )
      )
    }
  }
}

export function* addNewVoucher({ setupformId, values }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const apiActions = {
    actions: [
      {
        type: "create",
        data: {
          start_time: values.voucherValidTo,
          ticket_count: values.ticketCount,
          instances: [
            {
              offsale_time: values.offsaleTime,
              onsale_time: values.onsaleTime,
              voucher_valid_from: values.voucherValidFrom,
              voucher_valid_to: values.voucherValidTo,
              initial_price: values.initialPrice,
              discount_price: values.discountPrice,
              ticket_type: values.ticketType,
              ticket_increment: "1",
              commission: values.commission,
            }
          ]
        }
      }
    ]
  }

  const { response } = yield race({
    call: call(bulkUpdateSetupformInstanceGroups, setupformId, partnerId, apiActions),
    response: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.SUCCESS),
    error: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.FAILURE),
  })

  if (response) {
    yield put(reset('setupformVouchers__new'))
    yield put(actions.scrollPage(0, 0))
  }
}

export function* editVoucher({ setupformId, instanceGroupId, values }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const apiActions = {
    actions: [
      {
        type: "update",
        id: instanceGroupId,
        data: {
          start_time: values.voucherValidTo,
          ticket_count: values.ticketCount,
          instances: [
            {
              id: values.instanceId,
              offsale_time: values.offsaleTime,
              onsale_time: values.onsaleTime,
              voucher_valid_from: values.voucherValidFrom,
              voucher_valid_to: values.voucherValidTo,
              initial_price: values.initialPrice,
              discount_price: values.discountPrice,
              ticket_type: values.ticketType,
              ticket_increment: "1",
              commission: values.commission,
            }
          ]
        }
      }
    ]
  }

  const { response } = yield race({
    call: call(bulkUpdateSetupformInstanceGroups, setupformId, partnerId, apiActions),
    response: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.SUCCESS),
    error: take(actions.SETUPFORM_BULK_UPDATE_INSTANCE_GROUPS_API.FAILURE),
  })

  if (response) {
    yield put(hideModal())
  }
}

export function* updateSetupformVouchers({ response }) {
  const existingSetupForm = yield select(getCurrentSetupForm)
  const modifiedSetupForm = {
    ...existingSetupForm,
  }
  const setupforms = {}
  setupforms[modifiedSetupForm.id] = modifiedSetupForm

  for (let index = 0; index < response.length; index += 1) {
    const responseInstanceGroup = response[index]

    const existingItemIndex = modifiedSetupForm.instanceGroups.findIndex(i => i.id === responseInstanceGroup.id)
    if (existingItemIndex >= 0) {
      modifiedSetupForm.instanceGroups[existingItemIndex] = responseInstanceGroup
    } else {
      modifiedSetupForm.instanceGroups.push(responseInstanceGroup)
    }
  }

  yield put(
    entityUpdate({
      response: {
        entities: {
          setupforms
        }
      }
    })
  )
}

export function* submitSetupformSaga({ setupformId }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)

  const { response } = yield race({
    call: call(submitSetupform, setupformId, partnerId),
    response: take(actions.ENTITIES_SETUPFORM.SUCCESS),
    error: take(actions.ENTITIES_SETUPFORM.FAILURE),
  })

  if (response) {
    redirectAbsolutePath(getPartnerUrl(partnerId))
  }
}

export function* updateVenueSearchResults({ query }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  yield call(fetchVenueSearchResults, partnerId, query)
}

export function* setAsNoVenue() {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const { apiResponse } = yield race({
    call: call(fetchVenueSearchResults, partnerId, 'No Venue'),
    apiResponse: take(actions.ENTITIES_VENUE_SEARCH_RESULTS.SUCCESS),
    error: take(actions.ENTITIES_VENUE_SEARCH_RESULTS.FAILURE),
  })

  if (apiResponse) {
    const venues = Object.entries(apiResponse.response.entities.venues).filter(v => v[1].isNoVenue)
    if (venues.length === 1) {
      yield put(change("setupform", "venueId", venues[0][0]))
    } else {
      yield put(
        actions.showErrorMessage(
          'Could not find a venue labelled as "No Venue". Please ensure that it exists in this market.'
        )
      )
    }
  }
}

export function* uploadImageSaga({ imagePreview }) {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const setupformId = parseInt(yield select(getCurrentSetupformId), 10)

  const data = new window.FormData()
  data.append('image', toBlob(imagePreview), 'image.jpg')

  yield call(uploadImage, partnerId, setupformId, data)
}

export function* deleteImageSaga() {
  const partnerId = parseInt(yield select(getCurrentPartnerId), 10)
  const setupformId = parseInt(yield select(getCurrentSetupformId), 10)
  const imageId = parseInt(yield select(getImageId), 10)

  yield call(deleteImage, partnerId, setupformId, imageId)
}



/** WEBPACK FOOTER **
 ** ./src/sagas/setupform/setupform-sagas.js
 **/